import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Comprehensive ResizeObserver error suppression
// This is a known benign issue with React + Radix UI Select components
// It does not affect functionality and is safe to suppress
const resizeObserverErrRe = /ResizeObserver loop completed with undelivered notifications|ResizeObserver loop limit exceeded/i;

// Suppress console.error
const origError = window.console.error;
window.console.error = (...args) => {
  if (args && args.length > 0) {
    const errorMessage = typeof args[0] === 'string' ? args[0] : (args[0]?.message || String(args[0]));
    if (resizeObserverErrRe.test(errorMessage)) {
      return;
    }
  }
  origError.call(window.console, ...args);
};

// Suppress console.warn for ResizeObserver
const origWarn = window.console.warn;
window.console.warn = (...args) => {
  if (args && args.length > 0) {
    const warnMessage = typeof args[0] === 'string' ? args[0] : String(args[0]);
    if (resizeObserverErrRe.test(warnMessage)) {
      return;
    }
  }
  origWarn.call(window.console, ...args);
};

// Suppress global error event
window.addEventListener('error', (e) => {
  if (e.message && resizeObserverErrRe.test(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
}, true);

// Suppress unhandledrejection for ResizeObserver
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason && e.reason.message && resizeObserverErrRe.test(e.reason.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
}, true);

// Override ResizeObserver to catch and suppress errors at source
if (typeof ResizeObserver !== 'undefined') {
  const OriginalResizeObserver = ResizeObserver;
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        try {
          callback(entries, observer);
        } catch (e) {
          if (!resizeObserverErrRe.test(e.message)) {
            throw e;
          }
          // Suppress ResizeObserver loop errors
        }
      });
    }
  };
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register the PWA service worker (production only) for installability + light offline shell.
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('Service worker registration failed:', err);
    });
  });
}
